// Logistics task-queue presets — one per tab in LogisticsTaskQueuePage.
// Row scope (mine / unassigned / all) is governed by the assignment quick
// filter wired in logisticsQueueConfig, not a hard `myEmployee` permanent
// filter — so the user can widen each tab beyond their own assignments.

import type { CrudFilter } from "@refinedev/core";
import { nextWorkingDay } from "@shared/utils/workingDays";
import { TASK_ACTIVE, TASK_BASE_COLS, pendingCheck, taskPreset } from "./presetUtils";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const tomorrow = () => daysAgo(-1);

// Logistics alerts look at transactions loading up to the NEXT WORKING DAY and
// no older than two weeks back — near enough that an upcoming load / transport
// gap needs attention, recent enough that an unsent unload notification is still
// actionable. Bounds the list (and the `*_check` computation) so stale backlog
// never floods it.
const LOGISTICS_ALERT_LOOKBACK_DAYS = 14;

// A missing loading photo is only a queue task while the load is recent enough
// that the operator can still get the picture.
const PHOTO_WINDOW_DAYS = 7;

// How far back the "Dok. i awizacje" tab reaches on the unload side before a
// row ages out of it (owner, 2026-08-24).
const DOCS_UNLOAD_FLOOR_DAYS = 7;

/** Document tabs only list transactions unloaded at least two days ago —
 *  anything fresher hasn't realistically had time to receive paperwork. NULL
 *  `unload_date` falls through the `lte` comparison and is excluded too. */
const deliveredAtLeast2dAgo = (): CrudFilter => ({
  field: "unload_date", operator: "lte", value: daysAgo(2),
});

// ── Action tabs ────────────────────────────────────────────────────────

// Transport = the "dodaj transport" work only. A row whose transport reads
// "Gotowy" (transport_complete_check completed) is not this tab's business even
// when its transport ORDER is still ungenerated/unsent — those two checks live
// in the docs tab below, which is where "niewygenerowane zlecenia" belong.
export const logisticsTransportPreset = taskPreset(
  "logistics_transport",
  [
    ...TASK_BASE_COLS, "carriers", "transport_action", "generated_docs_transport_order", "logistics_emails_transport_order",
    // Moved here from the removed "Nieprzypisane" tab (issue #306) so the
    // queue keeps a one-click assign affordance.
    "logistics_employee_assign",
  ],
  [...TASK_ACTIVE, pendingCheck("transport_complete_check")],
);

// Docs + awizacje = every order or notification still ungenerated/unsent, on
// loads due no later than tomorrow.
//
// Issue #298: the tab exists so a logistician can send the awizacja and the
// loading documents, and that work is due against the LOAD — so the window is
// `load_date <= today + 1` (tomorrow's loads, today's, and every earlier one
// still outstanding). It widened the #270 `unload_date >= yesterday` bound,
// which dropped a load whose unload had already slipped past even though its
// awizacja was never sent — see the floor below for where that bound landed.
// Plain +1 day, not `nextWorkingDay` — that widening belongs to the alert
// window (#268), and #298 asks for "aktualna data + 1".
//
// Floored at 7 days on the UNLOAD side (owner, 2026-08-24). #298 left the tab
// open into the past and in production that made it far too long — every
// historical transaction with an outstanding doc check piled up. The two bounds
// read different columns ON PURPOSE: the tab opens against the load
// (`load_date <= today + 1`) and ages out against the unload
// (`unload_date >= today - 7`), so a load that happened long ago but unloads
// inside the window still belongs here. NULL `unload_date` falls through the
// `gte` comparison and is excluded, same as in `deliveredAtLeast2dAgo()` — no
// such row satisfies the rest of this predicate on prod today, but a duplicated
// or Excel-imported transaction can carry one.
//
// Membership still spans all six pending checks below (owner ruling,
// 2026-08-21) — reading #298 as "unsent awizacja załadunku only" would drop the
// transport-order / annex7 / CMR / unload-notification rows off the only tab
// that lists them.
export const logisticsDocsPreset = taskPreset(
  "logistics_docs",
  [...TASK_BASE_COLS, "carriers", "drivers", "transport_action", "generated_docs", "logistics_emails"],
  [
    ...TASK_ACTIVE,
    { field: "transport_complete_check", operator: "eq", value: "completed" },
    { field: "load_date", operator: "lte", value: tomorrow() },
    { field: "unload_date", operator: "gte", value: daysAgo(DOCS_UNLOAD_FLOOR_DAYS) },
    {
      key: "logistics_docs_any_pending",
      operator: "or", value: [
        pendingCheck("transport_order_generated_check"),
        pendingCheck("transport_order_sent_check"),
        pendingCheck("annex7_generated_check"),
        pendingCheck("cmr_generated_check"),
        pendingCheck("loading_notification_sent_check"),
        pendingCheck("unloading_notification_sent_check"),
      ],
    },
  ],
);

// Photos are only realistically uploadable for about a week after loading;
// beyond that the operator reaches the transaction by IRM number instead. Older
// gaps still count as misses in the report — they just aren't a queue task.
//
// The "loading photo still missing" predicate, shared by the queue tab and the
// report so the two can never disagree about WHICH rows count as a photo gap —
// they differ only in the date window applied on top (see below).
const PHOTO_GAP: CrudFilter[] = [
  ...TASK_ACTIVE,
  pendingCheck("loading_photo_approved_check"),
  // Issue #295: a load that has not happened yet cannot be missing its photo.
  // Lives in the SHARED fragment, not the tab preset, because the report
  // counterpart wants the same rule (there it is a no-op — anything older than
  // PHOTO_WINDOW_DAYS is already in the past).
  { field: "load_date", operator: "lte", value: today() },
];

export const logisticsPhotosPreset = taskPreset(
  "logistics_photos",
  [...TASK_BASE_COLS, "photos"],
  [
    ...PHOTO_GAP,
    { field: "load_date", operator: "gte", value: daysAgo(PHOTO_WINDOW_DAYS) },
  ],
  {
    // Issue #295: the tab is read as one date-ordered list — newest load first,
    // so today's loads form an unbroken block at the top. The `priority desc`
    // pin is dropped for this tab in logisticsQueueConfig (`pinPriority: false`)
    // so nothing floats above that block.
    defaultSort: [
      { field: "load_date",        order: "desc" },
      { field: "reference_number", order: "desc" },
    ],
  },
);

/** Report-side counterpart of `logisticsPhotosPreset` — the photo gaps the queue
 *  tab deliberately does NOT list. Same gap predicate (now also bounded above by
 *  `load_date <= today`, so future loads are excluded from both sides too),
 *  complementary window on the lower/older side: the tab shows loads within
 *  `PHOTO_WINDOW_DAYS` (still chaseable), the report counts the ones older than
 *  that (missed on time — issue #270 point 4's explicit intent). `lt` is the
 *  exact complement of the tab's `gte`, so every photo gap at or before today
 *  lands in exactly one of the two; NULL `load_date` falls through both
 *  comparisons and is excluded from each, which is intended (no load date = no
 *  deadline to have missed). */
export function logisticsMissedPhotosFilters(): CrudFilter[] {
  return [
    ...PHOTO_GAP,
    { field: "load_date", operator: "lt", value: daysAgo(PHOTO_WINDOW_DAYS) },
  ];
}

// Issue #305 pt 3: "Kopie dok." lists only IRMs loaded on or after this date;
// earlier loads are hidden from the tab (and, through the shared filter, from
// the backlog report's copies metric). A fixed calendar floor, not a rolling
// window. NULL `load_date` falls through the `gte` comparison and is excluded.
export const COPY_DOCS_LOAD_FLOOR = "2026-05-21";

// Missing copies (issue #288). The gap is evaluated per document type — Aneks
// VII, CMR, kwit wagowy — by `copies_missing_status`: a type is covered by any
// uploaded copy (its review status is irrelevant here; a bad copy is the
// "Odrzucone" tab's business) or by a non-rejected original of the SAME type.
// Both the old annex-only `annex_copy_approved_check` gate and the annex7+CMR
// `original_docs_status` roll-up are subsumed by that column. On a transport
// leaving the EU only Aneks VII counts, and any original of it covers (#305) —
// see the copy-coverage block in shared/checks/checkRegistry.ts.
export const logisticsCopyDocsPreset = taskPreset(
  "logistics_copy_docs",
  [...TASK_BASE_COLS, "drivers", "transport_action", "copy_docs", "days_since_delivery", "last_reminder_sent"],
  [
    ...TASK_ACTIVE,
    { field: "copies_missing_status", operator: "eq", value: "missing" },
    deliveredAtLeast2dAgo(),
    { field: "load_date", operator: "gte", value: COPY_DOCS_LOAD_FLOOR },
  ],
  { resourceKeyVersion: 4 },
);

// Rejected copies (issue #289) — by the AI verifier or by an admin; both write
// transaction_files.review_status = 'not_approved'. `copies_rejected_status`
// reads that across all three required document types and cancels a rejection
// once a non-rejected original of the SAME type is on file. The tri-state
// annex_copy_approved_check cannot express this (not_required | completed |
// pending only), so filtering it for 'rejected' left this tab permanently empty.
export const logisticsRejectedCopiesPreset = taskPreset(
  "logistics_rejected_copies",
  [...TASK_BASE_COLS, "copy_docs", "days_since_delivery"],
  [
    ...TASK_ACTIVE,
    { field: "copies_rejected_status", operator: "eq", value: "rejected" },
  ],
  { resourceKeyVersion: 4 },
);

// Reminders split in two by whether the carrier has already been chased once.
// Both sections share one population — nothing uploaded at all (no copies, no
// originals), at least two days after unload. That is deliberately a STRICT
// SUBSET of "Kopie dokumentów": a chase e-mail is only worth sending when the
// carrier has sent nothing whatsoever, whereas the copies tab also lists rows
// where only one of the three required documents is still outstanding.
const REMINDER_COLS = [
  ...TASK_BASE_COLS, "carriers", "days_since_unload", "copy_docs",
  "last_reminder_sent", "send_reminder",
];

const REMINDER_NOTHING_UPLOADED = [
  { field: "transport_complete_check", operator: "eq", value: "completed" },
  { field: "annex_copy_status", operator: "eq", value: "missing" },
  { field: "original_docs_status", operator: "eq", value: "awaiting_upload" },
  deliveredAtLeast2dAgo(),
] as CrudFilter[];

export const logisticsDocRemindersNotSentPreset = taskPreset(
  "logistics_doc_reminders_not_sent",
  REMINDER_COLS,
  [
    ...TASK_ACTIVE,
    ...REMINDER_NOTHING_UPLOADED,
    { field: "documents_reminder_ever_sent", operator: "eq", value: false },
  ],
  { bulkActions: ["bulk-send-reminder"] },
);

export const logisticsDocRemindersSentPreset = taskPreset(
  "logistics_doc_reminders_sent",
  REMINDER_COLS,
  [
    ...TASK_ACTIVE,
    ...REMINDER_NOTHING_UPLOADED,
    { field: "documents_reminder_ever_sent", operator: "eq", value: true },
    // Already chased today → not today's task; it reappears tomorrow.
    { field: "documents_reminder_sent_today", operator: "eq", value: false },
  ],
  { bulkActions: ["bulk-send-reminder"] },
);

// ── Logistics alerts (loads + unsent unload notifications) ────────────────
//
// One alert list over `transactions_with_checks` (the same source every
// logistics tab reads) surfacing two kinds of outstanding work:
//   1. Pre-load readiness — a load due soon still missing a pre-departure
//      requirement (transport completeness, transport order generated + sent,
//      loading notification, annex7 + CMR generated).
//   2. Unsent unload notifications — the awizacja na rozładunek and the
//      wjazdówka (entry request) that a required transaction hasn't sent yet.
//      Those transactions have already loaded, so they fall inside the same
//      window (load_date from two weeks ago up to the next working day) and
//      ride along.
//
// The supabase data provider (@refinedev/supabase 6.0.1) supports neither a
// top-level `and` nor nested and/or inside an `or`, so this cannot be per-branch
// scoped (e.g. imminent loads AND unload-notifications any date). Instead it is
// one bounded window — `load_date` from two weeks ago up to the NEXT WORKING DAY
// — plus a single flat `or` of pending checks (pre-load readiness incl.
// transport not ready, AND the unsent unload notifications). The window keeps
// the row set small so PostgREST never computes the expensive `*_check` columns
// over stale backlog.
//
// The upper bound is the next working day rather than a plain tomorrow (issue
// #268): on a Friday the operator must already see Monday's outstanding work,
// and the Thursday before a public holiday must reach past it. Weekend and
// holiday loads inside the widened span ride along, which is intended — they
// are outstanding too.
//
// Exported as a function so the sider badge counts the exact same rows the
// page lists (single source of truth, no drift).

export const PRE_LOAD_PENDING_CHECKS = [
  "transport_complete_check",
  "transport_order_generated_check",
  "transport_order_sent_check",
  "loading_notification_sent_check",
  "annex7_generated_check",
  "cmr_generated_check",
] as const;

// Unload notifications an operator sends after loading, before unload:
// the awizacja na rozładunek and the wjazdówka (site entry request).
export const UNLOAD_NOTIFICATION_PENDING_CHECKS = [
  "unloading_notification_sent_check",
  "unload_entry_request_sent_check",
] as const;

export function logisticsLoadAlertsFilters(): CrudFilter[] {
  return [
    ...TASK_ACTIVE,
    { field: "load_date", operator: "gte", value: daysAgo(LOGISTICS_ALERT_LOOKBACK_DAYS) },
    { field: "load_date", operator: "lte", value: nextWorkingDay(today()) },
    {
      key: "logistics_alert_any_pending",
      operator: "or",
      value: [
        ...PRE_LOAD_PENDING_CHECKS.map(pendingCheck),
        ...UNLOAD_NOTIFICATION_PENDING_CHECKS.map(pendingCheck),
      ],
    },
  ];
}

export const logisticsLoadAlertsPreset = taskPreset(
  "logistics_load_alerts",
  [
    ...TASK_BASE_COLS, "checklist_progress",
    "carriers", "drivers", "vehicles", "transport_action",
    "generated_docs", "logistics_emails",
  ],
  logisticsLoadAlertsFilters(),
  {
    defaultSort: [
      { field: "load_date", order: "asc" },
      { field: "reference_number", order: "desc" },
    ],
  },
);

